import {
  use,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import type {
  ContextLink,
  DocumentModel,
} from "@bindery/shared/corpus/document";
import { cn } from "@bindery/ui/lib/utils";
import { CONTEXT_LINK_HINT_ID } from "./context-link-scope";
import { ReferenceLookupContext } from "./reference-lookup-context";

/** A drag that ends on a link is a text selection, not a request to open it. */
function selectionTouches(el: Element): boolean {
  const sel = window.getSelection();
  if (sel === null || sel.isCollapsed || sel.rangeCount === 0) return false;
  return sel.getRangeAt(0).intersectsNode(el);
}

/**
 * A linked phrase, opening the reference lookup over its complete span. The
 * trigger is a `span` with a button role rather than a `<button>`, because
 * browsers will not start or extend a text selection inside a button, and the
 * phrase has to stay ordinary, selectable prose. It adds no text and no
 * `data-start`: the runs inside it keep theirs, and `anchor-picking` takes
 * the outermost.
 */
export function ContextLinkTrigger({
  link,
  model,
  children,
}: {
  link: ContextLink;
  model: DocumentModel;
  children: ReactNode;
}) {
  const lookup = use(ReferenceLookupContext);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const keyboardActivation = useRef(false);

  const activate = (): void => {
    lookup?.openContextLink(link, model, triggerRef.current);
  };

  const onClick = (e: MouseEvent<HTMLSpanElement>): void => {
    if (!keyboardActivation.current && selectionTouches(e.currentTarget)) {
      return;
    }
    activate();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLSpanElement>): void => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    keyboardActivation.current = true;
    e.currentTarget.click();
    keyboardActivation.current = false;
  };

  const expanded = lookup?.activeLinkId === link.id;

  return (
    <span
      ref={triggerRef}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- browsers will not select text inside a <button>, and the phrase is prose
      role="button"
      tabIndex={0}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      aria-describedby={CONTEXT_LINK_HINT_ID}
      data-context-link={link.id}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={cn(
        "cursor-pointer rounded-[2px] underline decoration-dotted decoration-[0.08em] underline-offset-[0.22em] transition-colors",
        "decoration-foreground/40 hover:decoration-lookup hover:text-lookup",
        "focus-visible:ring-ring/60 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none",
        "aria-expanded:bg-lookup/10 aria-expanded:decoration-lookup",
      )}
    >
      {children}
    </span>
  );
}
